import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { apiService } from '@/services/api';
import { useAuth } from '../AuthContext';
import { storage } from '@/utils';

export interface Product {
  externalId: string;
  name: string;
  description: string;
  price: number;
  discountPrice: number;
  credits: number;
  createdAt: string;
  updatedAt: string;
}

interface ProductContextType {
  products: Product[];
  filteredAndSortedProducts: Product[];
  addToCart: (externalId: string) => void;
  filterReset: () => void;
  refetchPackages: () => Promise<void>;
  cartItems: any[];
  incrementQuantity: (externalId: string) => void;
  removeFromCart: (externalId: string) => void;
  decrementQuantity: (externalId: string) => void;
  clearCart: () => void;
  loading: boolean;
  error: string | null;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

const getCartStorageKey = (userId: string | null): string => {
  return userId ? `product_cart_${userId}` : 'product_cart_guest';
};

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoadRef = useRef(false);

  // Завантажуємо корзину користувача при зміні userId
  useEffect(() => {
    if (user?.id) {
      isInitialLoadRef.current = true;
      const storageKey = getCartStorageKey(user.id);
      const savedCart = storage.get<any[]>(storageKey, []);
      setCartItems(savedCart);
      // Після завантаження дозволяємо збереження
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    } else {
      setCartItems([]);
      isInitialLoadRef.current = false;
    }
  }, [user?.id]);

  // Зберігаємо корзину в localStorage при кожній зміні (тільки не під час завантаження)
  useEffect(() => {
    if (user?.id && !isInitialLoadRef.current) {
      const storageKey = getCartStorageKey(user.id);
      storage.set(storageKey, cartItems);
    }
  }, [cartItems, user?.id]);

  const fetchPackages = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCreditPackages();
      // Новий масив, щоб React завжди оновив UI (навіть якщо API повернув кешовану ссылку)
      setProducts([...response.packages]);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch credit packages:', err);
      setError(err.message || 'Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch credit packages on initial load
  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const filteredAndSortedProducts = [...products].reverse();// For now, no filtering

  const addToCart = (externalId: string) => {
    const product = products.find(p => p.externalId === externalId);
    if (product) {
      setCartItems(prevItems => {
        const existingItem = prevItems.find(item => item.externalId === externalId);
        if (existingItem) {
          return prevItems.map(item =>
            item.externalId === externalId ? { ...item, qty: item.qty + 1 } : item
          );
        } else {
          return [...prevItems, { ...product, qty: 1 }];
        }
      });
    }
  };

  const incrementQuantity = (externalId: string) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.externalId === externalId ? { ...item, qty: item.qty + 1 } : item
      )
    );
  };

  const decrementQuantity = (externalId: string) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.externalId === externalId ? { ...item, qty: Math.max(1, item.qty - 1) } : item
      )
    );
  };

  const removeFromCart = (externalId: string) => {
    setCartItems(prevItems => prevItems.filter(item => item.externalId !== externalId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const filterReset = () => {
    // Reset any filters if needed
  };

  const value: ProductContextType = {
    products,
    filteredAndSortedProducts,
    addToCart,
    filterReset,
    refetchPackages: fetchPackages,
    cartItems,
    incrementQuantity,
    removeFromCart,
    decrementQuantity,
    clearCart,
    loading,
    error,
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export { ProductContext };
